import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Paper,
    Alert,
    Stack,
    Chip,
    Card,
    CardContent,
    useTheme,
    Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import AccountSelector from '../common/AccountSelector';
import CostCenterSelector from '../common/CostCenterSelector';
import {
    CreateVoucherDto,
    CreateVoucherDetailDto,
    VoucherDto,
    UpdateVoucherDto,
} from '../../types/vouchers.types';
import { VOUCHER_SIDE } from '../../constants/vouchers.constants';
import { cashReceiptsService } from '../../services/cashReceipts.service';
import apiClient from '../../services/api';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { useGridEnterNavigation } from '../../hooks/useGridEnterNavigation';

interface CashReceiptDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (dto: CreateVoucherDto | UpdateVoucherDto) => Promise<void>;
    editVoucher?: VoucherDto | null;
    cashParentAccountId: number;
}

interface CreditLine {
    id: string;
    accountId: number | null;
    costCenterId: number | null;
    amount: number;
    description: string;
}

const CashReceiptDialog: React.FC<CashReceiptDialogProps> = ({
    open,
    onClose,
    onSave,
    editVoucher,
    cashParentAccountId,
}) => {
    const { language } = useLanguage();
    const isRTL = language === 'ar';
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const { minDate, maxDate } = useFiscalDateRange();

    const gridRef = useRef<HTMLDivElement>(null);
    useGridEnterNavigation(gridRef);

    // Form State
    const [voucherNumber, setVoucherNumber] = useState<string>('');
    const [voucherDate, setVoucherDate] = useState<string>(
        new Date().toISOString().split('T')[0]
    );
    const [payeeName, setPayeeName] = useState<string>('');
    const [notes, setNotes] = useState<string>('');

    // Debit Section (Single Line - Cash Account)
    const [debitAccountId, setDebitAccountId] = useState<number | null>(null);
    const [debitAmount, setDebitAmount] = useState<number>(0);

    // Credit Section (Multiple Lines)
    const [creditLines, setCreditLines] = useState<CreditLine[]>([
        {
            id: Math.random().toString(),
            accountId: null,
            costCenterId: null,
            amount: 0,
            description: '',
        },
    ]);

    const [loading, setLoading] = useState(false);
    const [checkingNumber, setCheckingNumber] = useState(false);
    const [error, setError] = useState<string>('');

    // Generate auto voucher number
    const generateAutoNumber = async () => {
        try {
            const year = new Date().getFullYear();
            const vouchers = await cashReceiptsService.getAll();

            const currentYearVouchers = vouchers.filter((v) =>
                v.voucherNumber.startsWith(`CR-${year}-`)
            );

            let maxNum = 0;
            currentYearVouchers.forEach((v) => {
                const parts = v.voucherNumber.split('-');
                if (parts.length === 3) {
                    const num = parseInt(parts[2]);
                    if (!isNaN(num) && num > maxNum) {
                        maxNum = num;
                    }
                }
            });

            const nextNum = (maxNum + 1).toString().padStart(4, '0');
            return `CR-${year}-${nextNum}`;
        } catch (err) {
            console.error('Failed to generate auto number:', err);
            return `CR-${new Date().getFullYear()}-0001`;
        }
    };

    // Check if voucher number exists
    const checkVoucherNumberExists = async (
        number: string
    ): Promise<boolean> => {
        try {
            const vouchers = await cashReceiptsService.getAll();
            return vouchers.some(
                (v) =>
                    v.voucherNumber === number &&
                    v.voucherId !== editVoucher?.voucherId
            );
        } catch (err) {
            console.error('Failed to check voucher number:', err);
            return false;
        }
    };

    // Reset form
    const resetForm = () => {
        setVoucherNumber('');
        setVoucherDate(new Date().toISOString().split('T')[0]);
        setPayeeName('');
        setNotes('');
        setDebitAccountId(null);
        setDebitAmount(0);
        setCreditLines([
            {
                id: Math.random().toString(),
                accountId: null,
                costCenterId: null,
                amount: 0,
                description: '',
            },
        ]);
        setError('');
    };

    // Load edit data
    const loadEditData = (voucher: VoucherDto) => {
        setVoucherNumber(voucher.voucherNumber);
        setVoucherDate(voucher.voucherDate.split('T')[0]);
        setPayeeName(voucher.payeeName || '');
        setNotes(voucher.notes || '');

        const debitDetail = voucher.details.find(
            (d) => d.side?.toLowerCase() === VOUCHER_SIDE.DEBIT.toLowerCase()
        );

        if (debitDetail) {
            setDebitAccountId(debitDetail.accountId || null);
            setDebitAmount(debitDetail.allocatedAmount);
        }

        const creditDetails = voucher.details.filter(
            (d) => d.side?.toLowerCase() === VOUCHER_SIDE.CREDIT.toLowerCase()
        );

        if (creditDetails.length > 0) {
            setCreditLines(
                creditDetails.map((d) => ({
                    id: Math.random().toString(),
                    accountId: d.accountId || null,
                    costCenterId: d.costCenterId || null,
                    amount: d.allocatedAmount,
                    description: d.description || '',
                }))
            );
        }
    };

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;
        if (open) {
            if (editVoucher) {
                timer = setTimeout(() => loadEditData(editVoucher), 100);
            } else {
                resetForm();
                generateAutoNumber().then(setVoucherNumber);
            }
        }
        return () => clearTimeout(timer);
    }, [open, editVoucher?.voucherId]);

    const handleRegenerateNumber = async () => {
        const newNumber = await generateAutoNumber();
        setVoucherNumber(newNumber);
    };

    // Credit Lines Management
    const addCreditLine = () => {
        setCreditLines([
            ...creditLines,
            {
                id: Math.random().toString(),
                accountId: null,
                costCenterId: null,
                amount: 0,
                description: '',
            },
        ]);
    };

    const removeCreditLine = (id: string) => {
        if (creditLines.length > 1) {
            setCreditLines(creditLines.filter((line) => line.id !== id));
        }
    };

    const updateCreditLine = (
        id: string,
        field: keyof CreditLine,
        value: any
    ) => {
        setCreditLines(
            creditLines.map((line) =>
                line.id === id ? { ...line, [field]: value } : line
            )
        );
    };

    // Check if account is already used
    const isAccountUsed = (
        accountId: number | null,
        currentLineId?: string
    ): boolean => {
        if (!accountId) return false;
        if (accountId === debitAccountId) return true;
        return creditLines.some(
            (line) => line.id !== currentLineId && line.accountId === accountId
        );
    };

    // Calculations
    const totalCredit = creditLines.reduce(
        (sum, line) => sum + (line.amount || 0),
        0
    );
    const totalDebit = debitAmount || 0;
    const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;
    const difference = totalDebit - totalCredit;

    // Auto-update debit amount
    useEffect(() => {
        setDebitAmount(totalCredit);
    }, [totalCredit]);

    // Validation
    const validate = async (): Promise<boolean> => {
        if (!voucherNumber.trim()) {
            setError(isRTL ? 'رقم السند مطلوب' : 'Voucher number is required');
            return false;
        }

        setCheckingNumber(true);
        const exists = await checkVoucherNumberExists(voucherNumber);
        setCheckingNumber(false);

        if (exists) {
            setError(
                isRTL
                    ? 'رقم السند مستخدم مسبقاً'
                    : 'Voucher number already exists'
            );
            return false;
        }

        if (!voucherDate) {
            setError(isRTL ? 'التاريخ مطلوب' : 'Date is required');
            return false;
        }

        if (!debitAccountId) {
            setError(isRTL ? 'الصندوق مطلوب' : 'Cash account is required');
            return false;
        }

        if (totalDebit <= 0) {
            setError(
                isRTL
                    ? 'المبلغ يجب أن يكون أكبر من صفر'
                    : 'Amount must be greater than zero'
            );
            return false;
        }

        if (!isBalanced) {
            setError(
                isRTL ? 'المدين يجب أن يساوي الدائن' : 'Debit must equal Credit'
            );
            return false;
        }

        const invalidCreditLines = creditLines.filter(
            (line) => !line.accountId || line.amount <= 0
        );
        if (invalidCreditLines.length > 0) {
            setError(
                isRTL
                    ? 'جميع الأسطر يجب أن تحتوي على حساب ومبلغ'
                    : 'All lines must have account and amount'
            );
            return false;
        }
        for (const line of creditLines) {
            if (line.accountId) {
                try {
                    const accountResponse = await apiClient.get(
                        '/ChartOfAccounts/' + line.accountId
                    );
                    const account = accountResponse.data.data;
                    if (
                        account &&
                        account.requireCostCenter &&
                        !line.costCenterId
                    ) {
                        const msg =
                            (isRTL ? 'الحساب' : 'Account') +
                            ' "' +
                            (isRTL
                                ? account.accountNameAr
                                : account.accountName) +
                            '" ' +
                            (isRTL
                                ? 'يتطلب مركز تكلفة'
                                : 'requires Cost Center');
                        setError(msg);
                        toast.warning(msg);
                        return false;
                    }
                } catch (err) {
                    console.error('Error validating account:', err);
                }
            }
        }

        setError('');
        return true;
    };

    // Save
    const handleSave = async () => {
        if (!(await validate())) return;

        try {
            setLoading(true);
            setError('');

            const details: CreateVoucherDetailDto[] = [];

            details.push({
                allocatedAmount: debitAmount,
                accountId: debitAccountId,
                side: VOUCHER_SIDE.DEBIT,
                costCenterId: null,
                description: notes,
            });

            creditLines.forEach((line) => {
                details.push({
                    allocatedAmount: line.amount,
                    accountId: line.accountId,
                    side: VOUCHER_SIDE.CREDIT,
                    costCenterId: line.costCenterId,
                    description: line.description,
                });
            });

            if (editVoucher) {
                const updateDto: UpdateVoucherDto = {
                    voucherId: editVoucher.voucherId,
                    voucherNumber,
                    voucherDate,
                    voucherType: 'CR',
                    payeeName: payeeName.trim() || undefined,
                    amount: totalDebit,
                    currencyCode: 'KWD',
                    exchangeRate: 1,
                    notes,
                    details,
                };
                await onSave(updateDto);
            } else {
                const createDto: CreateVoucherDto = {
                    voucherNumber,
                    voucherDate,
                    voucherType: 'CR',
                    payeeName: payeeName.trim() || undefined,
                    amount: totalDebit,
                    currencyCode: 'KWD',
                    exchangeRate: 1,
                    notes,
                    details,
                };
                await onSave(createDto);
            }

            toast.success(isRTL ? 'تم الحفظ بنجاح' : 'Saved successfully');
            handleClose();
        } catch (err: any) {
            const errorMsg =
                err.message || (isRTL ? 'حدث خطأ' : 'An error occurred');
            setError(errorMsg);
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xl"
            fullWidth
            dir={isRTL ? 'rtl' : 'ltr'}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    background: isDarkMode
                        ? 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)'
                        : 'linear-gradient(135deg, #e3ffe7 0%, #d9e7ff 100%)',
                    height: '95vh',
                    border: isDarkMode
                        ? '1px solid rgba(255,255,255,0.1)'
                        : 'none',
                },
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    background: isDarkMode
                        ? 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)'
                        : 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 2.5,
                    borderBottom: isDarkMode
                        ? '1px solid rgba(255,255,255,0.1)'
                        : 'none',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <MonetizationOnIcon sx={{ fontSize: 36 }} />
                    <Box
                        component="span"
                        sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}
                    >
                        {editVoucher
                            ? isRTL
                                ? 'تعديل سند قبض نقدي'
                                : 'Edit Cash Receipt'
                            : isRTL
                              ? 'سند قبض نقدي جديد'
                              : 'New Cash Receipt'}
                    </Box>
                </Box>
                <IconButton onClick={handleClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ mt: 3, overflowY: 'auto' }}>
                <Stack spacing={3}>
                    {/* Error Alert */}
                    {error && (
                        <Alert
                            severity="error"
                            onClose={() => setError('')}
                            sx={{ borderRadius: 2 }}
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Voucher Info Card */}
                    <Card
                        elevation={4}
                        sx={{
                            borderRadius: 3,
                            bgcolor: isDarkMode
                                ? 'rgba(255,255,255,0.05)'
                                : 'white',
                            border: isDarkMode
                                ? '1px solid rgba(255,255,255,0.1)'
                                : 'none',
                            boxShadow: isDarkMode
                                ? 'none'
                                : '0 8px 32px rgba(17, 153, 142, 0.15)',
                        }}
                    >
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                <ReceiptLongIcon
                                    sx={{
                                        color: isDarkMode
                                            ? '#38ef7d'
                                            : '#11998e',
                                        fontSize: 28,
                                    }}
                                />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: isDarkMode
                                            ? '#38ef7d'
                                            : '#11998e',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {isRTL
                                        ? 'معلومات السند'
                                        : 'Voucher Information'}
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                <TextField
                                    label={
                                        isRTL ? 'رقم السند' : 'Voucher Number'
                                    }
                                    value={voucherNumber}
                                    onChange={(e) =>
                                        setVoucherNumber(e.target.value)
                                    }
                                    fullWidth
                                    required
                                    placeholder="CR-2026-0001"
                                    InputProps={{
                                        endAdornment: !editVoucher && (
                                            <IconButton
                                                size="small"
                                                onClick={handleRegenerateNumber}
                                                title={
                                                    isRTL
                                                        ? 'إعادة توليد الرقم'
                                                        : 'Regenerate Number'
                                                }
                                            >
                                                <RefreshIcon />
                                            </IconButton>
                                        ),
                                    }}
                                    sx={{
                                        bgcolor: isDarkMode
                                            ? 'rgba(255,255,255,0.05)'
                                            : 'white',
                                        '& .MuiInputLabel-root': {
                                            color: isDarkMode
                                                ? 'rgba(255,255,255,0.7)'
                                                : 'inherit',
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            color: isDarkMode
                                                ? 'white'
                                                : 'inherit',
                                            '& fieldset': {
                                                borderColor: isDarkMode
                                                    ? 'rgba(255,255,255,0.23)'
                                                    : 'inherit',
                                            },
                                        },
                                    }}
                                />
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label={isRTL ? 'التاريخ' : 'Date'}
                                        value={voucherDate ? new Date(voucherDate) : null}
                                        onChange={(date) => setVoucherDate(date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '')}
                                        format="dd/MM/yyyy"
                                        views={['year', 'month', 'day']}
                                        openTo="day"
                                        minDate={minDate}
                                        maxDate={maxDate}
                                        slotProps={{ textField: { fullWidth: true, required: true, sx: { bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'white', '& .MuiInputLabel-root': { color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'inherit' }, '& .MuiOutlinedInput-root': { color: isDarkMode ? 'white' : 'inherit', '& fieldset': { borderColor: isDarkMode ? 'rgba(255,255,255,0.23)' : 'inherit' } } } } }}
                                    />
                                </LocalizationProvider>
                            </Box>
                            <TextField
                                label={isRTL ? 'اسم الدافع' : 'Payer Name'}
                                value={payeeName}
                                onChange={(e) => setPayeeName(e.target.value)}
                                fullWidth
                                placeholder={
                                    isRTL
                                        ? 'أدخل اسم الشخص أو الشركة الدافعة'
                                        : 'Enter payer name or company'
                                }
                                sx={{
                                    mt: 2,
                                    bgcolor: isDarkMode
                                        ? 'rgba(255,255,255,0.05)'
                                        : 'white',
                                    '& .MuiInputLabel-root': {
                                        color: isDarkMode
                                            ? 'rgba(255,255,255,0.7)'
                                            : 'inherit',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        color: isDarkMode ? 'white' : 'inherit',
                                        '& fieldset': {
                                            borderColor: isDarkMode
                                                ? 'rgba(255,255,255,0.23)'
                                                : 'inherit',
                                        },
                                    },
                                }}
                            />
                            <TextField
                                label={isRTL ? 'ملاحظات' : 'Notes'}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                fullWidth
                                multiline
                                rows={2}
                                sx={{
                                    mt: 2,
                                    bgcolor: isDarkMode
                                        ? 'rgba(255,255,255,0.05)'
                                        : 'white',
                                    '& .MuiInputLabel-root': {
                                        color: isDarkMode
                                            ? 'rgba(255,255,255,0.7)'
                                            : 'inherit',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        color: isDarkMode ? 'white' : 'inherit',
                                        '& fieldset': {
                                            borderColor: isDarkMode
                                                ? 'rgba(255,255,255,0.23)'
                                                : 'inherit',
                                        },
                                    },
                                }}
                            />
                        </CardContent>
                    </Card>

                    {/* Debit Section */}
                    <Card
                        elevation={5}
                        sx={{
                            borderRadius: 3,
                            background: isDarkMode
                                ? 'linear-gradient(135deg, rgba(56,239,125,0.15) 0%, rgba(17,153,142,0.15) 100%)'
                                : 'linear-gradient(135deg, #c2e9fb 0%, #a1c4fd 100%)',
                            border: '2px solid',
                            borderColor: isDarkMode
                                ? 'rgba(56,239,125,0.5)'
                                : '#11998e',
                            boxShadow: isDarkMode
                                ? 'none'
                                : '0 8px 32px rgba(17, 153, 142, 0.25)',
                        }}
                    >
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                <AccountBalanceIcon
                                    sx={{
                                        color: isDarkMode
                                            ? '#38ef7d'
                                            : '#11998e',
                                        fontSize: 28,
                                    }}
                                />
                                <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{
                                        color: isDarkMode
                                            ? '#38ef7d'
                                            : '#11998e',
                                    }}
                                >
                                    {isRTL
                                        ? '📥 الطرف المدين (إلى الصندوق)'
                                        : '📥 Debit Side (To Cash)'}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'flex-start',
                                }}
                            >
                                <Box sx={{ flex: 1 }}>
                                    <AccountSelector
                                        value={debitAccountId}
                                        onChange={setDebitAccountId}
                                        label={
                                            isRTL ? 'الصندوق' : 'Cash Account'
                                        }
                                        required
                                        parentAccountId={cashParentAccountId}
                                        onlyFinalAccounts={true}
                                    />
                                </Box>
                                <TextField
                                    label={isRTL ? 'المبلغ' : 'Amount'}
                                    type="number"
                                    value={debitAmount}
                                    disabled
                                    sx={{
                                        width: 200,
                                        '& .MuiInputBase-root': {
                                            bgcolor: isDarkMode
                                                ? 'rgba(56,239,125,0.2)'
                                                : 'rgba(255,255,255,0.9)',
                                            color: isDarkMode
                                                ? '#38ef7d'
                                                : '#11998e',
                                            fontWeight: 'bold',
                                            fontSize: '1.1rem',
                                        },
                                    }}
                                    InputProps={{
                                        inputProps: { min: 0, step: 0.001 },
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Credit Section */}
                    <Card
                        elevation={5}
                        sx={{
                            borderRadius: 3,
                            background: isDarkMode
                                ? 'linear-gradient(135deg, rgba(255,107,107,0.15) 0%, rgba(255,159,64,0.15) 100%)'
                                : 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                            border: '2px solid',
                            borderColor: isDarkMode
                                ? 'rgba(255,107,107,0.5)'
                                : '#ff6b6b',
                            boxShadow: isDarkMode
                                ? 'none'
                                : '0 8px 32px rgba(255, 107, 107, 0.25)',
                        }}
                    >
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <TrendingDownIcon
                                        sx={{
                                            color: isDarkMode
                                                ? '#ff9f40'
                                                : '#ff6b6b',
                                            fontSize: 28,
                                        }}
                                    />
                                    <Typography
                                        variant="h6"
                                        fontWeight="bold"
                                        sx={{
                                            color: isDarkMode
                                                ? '#ff9f40'
                                                : '#ff6b6b',
                                        }}
                                    >
                                        {isRTL
                                            ? '📤 الطرف الدائن (من)'
                                            : '📤 Credit Side (From)'}
                                    </Typography>
                                </Box>
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={addCreditLine}
                                    variant="contained"
                                    size="small"
                                    sx={{
                                        background: isDarkMode
                                            ? 'linear-gradient(45deg, #f093fb 30%, #f5576c 90%)'
                                            : 'linear-gradient(45deg, #FA8BFF 30%, #2BD2FF 90%)',
                                        boxShadow:
                                            '0 3px 5px 2px rgba(250, 139, 255, .3)',
                                    }}
                                >
                                    {isRTL ? 'إضافة سطر' : 'Add Line'}
                                </Button>
                            </Box>

                            <Box ref={gridRef}>
                            <Stack spacing={2}>
                                {creditLines.map((line, index) => (
                                    <Paper
                                        key={line.id}
                                        elevation={3}
                                        sx={{
                                            p: 2,
                                            bgcolor: isDarkMode
                                                ? 'rgba(255,255,255,0.05)'
                                                : 'rgba(255,255,255,0.9)',
                                            borderRadius: 2,
                                            border: '1px solid',
                                            borderColor: isDarkMode
                                                ? 'rgba(255,255,255,0.1)'
                                                : 'divider',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                alignItems: 'flex-start',
                                            }}
                                        >
                                            <Chip
                                                label={index + 1}
                                                color="error"
                                                sx={{
                                                    minWidth: 40,
                                                    fontWeight: 'bold',
                                                    bgcolor: isDarkMode
                                                        ? 'rgba(255,107,107,0.3)'
                                                        : '#ff6b6b',
                                                    color: isDarkMode
                                                        ? '#ff9f40'
                                                        : 'white',
                                                }}
                                            />

                                            <Box sx={{ flex: 2 }}>
                                                <AccountSelector
                                                    value={line.accountId}
                                                    onChange={(value) => {
                                                        if (
                                                            value &&
                                                            isAccountUsed(
                                                                value,
                                                                line.id
                                                            )
                                                        ) {
                                                            alert(
                                                                isRTL
                                                                    ? 'هذا الحساب مستخدم بالفعل!'
                                                                    : 'This account is already used!'
                                                            );
                                                            return;
                                                        }
                                                        updateCreditLine(
                                                            line.id,
                                                            'accountId',
                                                            value
                                                        );
                                                    }}
                                                    label={
                                                        isRTL
                                                            ? 'الحساب'
                                                            : 'Account'
                                                    }
                                                    required
                                                    onlyFinalAccounts={true}
                                                />
                                            </Box>

                                            <Box sx={{ flex: 1 }}>
                                                <CostCenterSelector
                                                    value={line.costCenterId}
                                                    onChange={(value) =>
                                                        updateCreditLine(
                                                            line.id,
                                                            'costCenterId',
                                                            value
                                                        )
                                                    }
                                                    label={
                                                        isRTL
                                                            ? 'مركز التكلفة'
                                                            : 'Cost Center'
                                                    }
                                                />
                                            </Box>

                                            <TextField
                                                label={
                                                    isRTL ? 'المبلغ' : 'Amount'
                                                }
                                                type="number"
                                                value={line.amount || ''}
                                                onChange={(e) =>
                                                    updateCreditLine(
                                                        line.id,
                                                        'amount',
                                                        parseFloat(
                                                            e.target.value
                                                        ) || 0
                                                    )
                                                }
                                                required
                                                sx={{
                                                    width: 150,
                                                    '& .MuiInputLabel-root': {
                                                        color: isDarkMode
                                                            ? 'rgba(255,255,255,0.7)'
                                                            : 'inherit',
                                                    },
                                                    '& .MuiOutlinedInput-root':
                                                        {
                                                            color: isDarkMode
                                                                ? 'white'
                                                                : 'inherit',
                                                        },
                                                }}
                                                InputProps={{
                                                    inputProps: {
                                                        min: 0,
                                                        step: 0.001,
                                                    },
                                                }}
                                            />

                                            <TextField
                                                label={
                                                    isRTL
                                                        ? 'الوصف'
                                                        : 'Description'
                                                }
                                                value={line.description}
                                                onChange={(e) =>
                                                    updateCreditLine(
                                                        line.id,
                                                        'description',
                                                        e.target.value
                                                    )
                                                }
                                                sx={{
                                                    flex: 1,
                                                    '& .MuiInputLabel-root': {
                                                        color: isDarkMode
                                                            ? 'rgba(255,255,255,0.7)'
                                                            : 'inherit',
                                                    },
                                                    '& .MuiOutlinedInput-root':
                                                        {
                                                            color: isDarkMode
                                                                ? 'white'
                                                                : 'inherit',
                                                        },
                                                }}
                                            />

                                            <IconButton
                                                color="error"
                                                onClick={() =>
                                                    removeCreditLine(line.id)
                                                }
                                                disabled={
                                                    creditLines.length === 1
                                                }
                                                sx={{ mt: 1 }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </Paper>
                                ))}
                            </Stack>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Totals Card */}
                    <Card
                        elevation={6}
                        sx={{
                            borderRadius: 3,
                            background: isBalanced
                                ? isDarkMode
                                    ? 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)'
                                    : 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                                : isDarkMode
                                  ? 'linear-gradient(135deg, #c31432 0%, #240b36 100%)'
                                  : 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                            color: 'white',
                            boxShadow: isBalanced
                                ? '0 10px 40px rgba(17, 153, 142, 0.4)'
                                : '0 10px 40px rgba(250, 112, 154, 0.4)',
                        }}
                    >
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                }}
                            >
                                <Typography variant="h5" fontWeight="bold">
                                    {isRTL ? '⚖️ الإجمالي' : '⚖️ Total'}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 3,
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography
                                            variant="caption"
                                            sx={{ opacity: 0.9 }}
                                        >
                                            {isRTL ? 'المدين' : 'Debit'}
                                        </Typography>
                                        <Typography
                                            variant="h4"
                                            fontWeight="bold"
                                        >
                                            {totalDebit.toFixed(3)}
                                        </Typography>
                                    </Box>
                                    <Typography variant="h3" fontWeight="bold">
                                        =
                                    </Typography>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography
                                            variant="caption"
                                            sx={{ opacity: 0.9 }}
                                        >
                                            {isRTL ? 'الدائن' : 'Credit'}
                                        </Typography>
                                        <Typography
                                            variant="h4"
                                            fontWeight="bold"
                                        >
                                            {totalCredit.toFixed(3)}
                                        </Typography>
                                    </Box>
                                    <Typography variant="h2" fontWeight="bold">
                                        {isBalanced ? '✅' : '❌'}
                                    </Typography>
                                </Box>
                            </Box>
                            {!isBalanced && Math.abs(difference) > 0.01 && (
                                <Alert
                                    severity="warning"
                                    sx={{
                                        mt: 2,
                                        bgcolor: isDarkMode
                                            ? 'rgba(255,255,255,0.15)'
                                            : 'rgba(255,255,255,0.9)',
                                        color: isDarkMode ? 'white' : 'inherit',
                                    }}
                                >
                                    {isRTL ? 'الفرق: ' : 'Difference: '}
                                    <strong>
                                        {Math.abs(difference).toFixed(3)}
                                    </strong>
                                    {difference > 0
                                        ? isRTL
                                            ? ' (المدين أكبر)'
                                            : ' (Debit is higher)'
                                        : isRTL
                                          ? ' (الدائن أكبر)'
                                          : ' (Credit is higher)'}
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Stack>
            </DialogContent>

            <DialogActions
                sx={{
                    p: 3,
                    bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'grey.100',
                    borderTop: isDarkMode
                        ? '1px solid rgba(255,255,255,0.1)'
                        : 'none',
                }}
            >
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    size="large"
                    sx={{
                        minWidth: 120,
                        color: isDarkMode ? 'white' : 'inherit',
                    }}
                >
                    {isRTL ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading || !isBalanced || checkingNumber}
                    size="large"
                    startIcon={loading || checkingNumber ? null : <SaveIcon />}
                    sx={{
                        minWidth: 120,
                        background: isDarkMode
                            ? 'linear-gradient(45deg, #11998e 30%, #38ef7d 90%)'
                            : 'linear-gradient(45deg, #56ab2f 30%, #a8e063 90%)',
                        boxShadow: '0 3px 5px 2px rgba(86, 171, 47, .3)',
                    }}
                >
                    {loading || checkingNumber
                        ? isRTL
                            ? 'جارٍ المعالجة...'
                            : 'Processing...'
                        : isRTL
                          ? 'حفظ'
                          : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CashReceiptDialog;
